/**
 * UI-only presentation mapping for notification types.
 *
 * This module contains NO business logic. It only translates a notification
 * type string into a label key, an icon, and a tone for visual styling.
 * If a type is unknown, we fall back to the notification's own severity.
 */
import {
  AlertTriangle,
  Award,
  Bell,
  Building2,
  CheckCircle,
  Info,
  Shield,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";

import type {
  NotificationInboxItem,
  NotificationSeverity,
} from "@/services/notifications";

export type NotificationTone = NotificationSeverity;

export interface NotificationPresentation {
  /** i18n key for the short source label (e.g. "Buddy", "System"). */
  labelKey: string;
  icon: LucideIcon;
  tone: NotificationTone;
}

const PRESETS: Record<string, NotificationPresentation> = {
  buddy_request_received: {
    labelKey: "notifications.label.buddy",
    icon: UserPlus,
    tone: "info",
  },
  buddy_request_created: {
    labelKey: "notifications.label.buddy",
    icon: UserPlus,
    tone: "info",
  },
  buddy_request_accepted: {
    labelKey: "notifications.label.buddy",
    icon: Users,
    tone: "success",
  },
  job_completed: {
    labelKey: "notifications.label.job",
    icon: CheckCircle,
    tone: "success",
  },
  job_failed: {
    labelKey: "notifications.label.job",
    icon: AlertTriangle,
    tone: "error",
  },
  badge_awarded: {
    labelKey: "notifications.label.badge",
    icon: Award,
    tone: "success",
  },
  org_membership_changed: {
    labelKey: "notifications.label.organization",
    icon: Building2,
    tone: "info",
  },
};

export function getNotificationPresentation(
  item: Pick<NotificationInboxItem, "type" | "severity" | "source_domain">,
): NotificationPresentation {
  const preset = PRESETS[item.type];
  if (preset) return preset;

  // Best-effort grouping by prefix / domain — still pure presentation.
  if (item.type.startsWith("buddy_")) {
    return {
      labelKey: "notifications.label.buddy",
      icon: Users,
      tone: item.severity ?? "info",
    };
  }
  if (item.type.startsWith("message_")) {
    return {
      labelKey: "notifications.label.message",
      icon: Info,
      tone: item.severity ?? "info",
    };
  }
  if (item.type.startsWith("system_") || item.source_domain === "system") {
    return {
      labelKey: "notifications.label.system",
      icon: Shield,
      tone: item.severity ?? "info",
    };
  }

  return {
    labelKey: "notifications.label.system",
    icon: Bell,
    tone: item.severity ?? "info",
  };
}

/** Tailwind class hint per tone for icon coloring. */
export const TONE_TEXT_CLASS: Record<NotificationTone, string> = {
  info: "text-primary",
  success: "text-emerald-500 dark:text-emerald-400",
  warning: "text-amber-500 dark:text-amber-400",
  error: "text-destructive",
};